/**
 * HostOffer.js — Marketing one-pager for property owners.
 * Small homestays, cottages, lodges, B&Bs.
 * A4 single-page PDF. WhatsApp CTA + list-your-property + PMS-download links.
 *
 * Bundled offer (three value props at one flat annual price):
 *   1. Your property page on GoHimalayan + WhatsApp direct booking
 *   2. Property Management App (OHSM) to run day-to-day
 *   3. Regular features on GoHimalayan's Instagram & Facebook pages
 *
 * Flat price: ₹11,999 / year — any property size.
 */
import React, { useRef } from 'react';
import { Printer, MessageCircle, Check, X, Mountain, ArrowRight } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const CTA_NUMBER = '9871155963';
const CTA_INTL = '919871155963';
const OHSM_LOGO = 'https://static.wixstatic.com/media/d5f536_1a88aa9139e8401bbd6cbd68b790254c~mv2.png/v1/fill/w_82,h_82,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Logo%2001.png';
const GH_BLUE = '#2C5F8D';
const APPLY_URL = 'https://gohimalayan.com/apply';
const OHSM_URL = 'https://www.ohsm.in/';
const WA_MSG = encodeURIComponent(
  "Hi GoHimalayan — I want to know more about the Host Bundle at ₹11,999 / year."
);
const WA_LINK = `https://wa.me/${CTA_INTL}?text=${WA_MSG}`;

/* Three value propositions — the pitch is only these three lines */
const VALUE_PROPS = [
  {
    n: '01',
    title: 'Your own page on GoHimalayan',
    subtitle: 'Direct WhatsApp booking. No commission.',
    body: 'A dedicated editorial page for your property — photos, story, location, WhatsApp CTA. Every guest enquiry lands straight on your phone. No middlemen. No commission on any booking.',
  },
  {
    n: '02',
    title: 'The OHSM Property Management App',
    subtitle: 'Bookings, billing, housekeeping, staff, expenses.',
    body: 'The whole back-office in one app on your phone. Replaces the paper register and Excel. Simple enough that your staff can use it from day one.',
  },
  {
    n: '03',
    title: 'Featured on GoHimalayan’s Instagram & Facebook',
    subtitle: 'We push your listing to our own audience — regularly.',
    body: 'Your property is included in our editorial calendar — reels, posts and stories on GoHimalayan’s Instagram & Facebook pages, seen by our community of high-intent Himalayan travellers. (This does NOT mean we manage your handles.)',
  },
];

/* Savings comparison — each row gets a tick */
const SAVINGS = [
  { label: 'Building your own website + booking system', v: '~ ₹40,000 one-time · ₹6,000 / year hosting' },
  { label: 'OHSM standalone (₹999 / room / year)', v: '~ ₹4,000 – ₹8,000 / year' },
  { label: 'Getting featured on a travel brand’s Instagram', v: '₹15K – ₹30K per paid post' },
  { label: 'Direct-booking WhatsApp funnel & lead capture', v: 'DIY — slow, or agency ₹15K+ / month' },
];

const EXCLUDED = [
  'Professional photography (you send us your photos, we place them beautifully)',
  'OTA distribution (Airbnb / MMT / Booking.com) — you keep running those yourself',
  'We do NOT manage your Instagram / Facebook handles — we feature you on ours',
];

export default function HostOffer() {
  const docRef = useRef(null);

  const download = () => {
    // Use browser's native print pipeline (reliable with Tailwind).
    window.print();
  };

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4">
      {/* Screen-only controls */}
      <div className="max-w-4xl mx-auto mb-6 flex flex-col sm:flex-row gap-3 items-center justify-between print:hidden">
        <p className="text-sm text-stone-500">Marketing one-pager · shareable on WhatsApp</p>
        <div className="flex gap-3">
          <a href={WA_LINK} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
             data-testid="host-offer-whatsapp">
            <MessageCircle className="h-4 w-4" /> WhatsApp us
          </a>
          <button onClick={download}
                  className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
                  data-testid="host-offer-download-pdf">
            <Printer className="h-4 w-4" /> Save as PDF
          </button>
        </div>
      </div>

      {/* A4-shaped document (210 × 297 mm) */}
      <div ref={docRef}
           id="host-offer-doc"
           className="mx-auto bg-white shadow-xl"
           style={{ width: '210mm', height: '297mm', padding: '10mm 14mm 8mm', boxSizing: 'border-box', overflow: 'hidden' }}>

        {/* ─── Header ─── */}
        <header className="flex items-center justify-between pb-4 border-b border-stone-300">
          <div className="flex items-center gap-2.5">
            <Mountain className="h-8 w-8" strokeWidth={2.5} style={{ color: GH_BLUE }} />
            <div>
              <p className="text-2xl font-bold tracking-tight leading-none" style={{ ...pf, color: GH_BLUE }}>GoHimalayan</p>
              <p className="text-[9px] tracking-[0.28em] uppercase text-stone-500 mt-2.5">Discover Unique Himalayan Stays</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-2">
              <img src={OHSM_LOGO} alt="OHSM" crossOrigin="anonymous"
                   style={{ width: '36px', height: '36px' }} />
              <div className="text-left">
                <p className="text-lg font-bold tracking-tight leading-none text-stone-900">OHSM</p>
                <p className="text-[8px] tracking-[0.12em] uppercase text-stone-500 mt-1">Simplifying Hospitality Operations</p>
              </div>
            </div>
            <p className="text-[8px] tracking-[0.3em] uppercase text-stone-400 mt-1.5">A GoHimalayan Venture</p>
          </div>
        </header>

        {/* ─── Title ─── */}
        <section className="pt-3">
          <p className="text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ color: GH_BLUE }}>Host Bundle · 2026 · For homestays, cottages, lodges &amp; B&amp;Bs</p>
          <h1 className="text-[24px] leading-[1.15] text-stone-900 mb-1.5" style={pf}>
            Your place, your story &mdash; <span style={{ whiteSpace: 'nowrap' }}>Beautifully told.</span><br/>
            Directly booked. <span className="italic">Simply run.</span>
          </h1>
          <p className="text-[11.5px] text-stone-700 leading-snug max-w-[170mm]">
            Three things every small Himalayan property needs, bundled into one
            flat annual price. No commissions. No revenue share. No monthly bills.
          </p>
        </section>

        {/* ─── The 3 value props — visual, prominent ─── */}
        <section className="pt-3.5">
          <div className="grid grid-cols-3 gap-3">
            {VALUE_PROPS.map((v) => (
              <div key={v.n} className="bg-stone-50 border border-stone-200 rounded-xl p-3 relative">
                <div className="flex items-start gap-2 mb-1.5">
                  <div className="flex items-center justify-center rounded-full flex-shrink-0"
                       style={{ backgroundColor: GH_BLUE, color: 'white', width: '20px', height: '20px' }}>
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="text-[8.5px] tracking-[0.25em] uppercase text-stone-400">{v.n}</p>
                    <p className="text-[12.5px] font-bold leading-tight" style={{ color: GH_BLUE }}>{v.title}</p>
                  </div>
                </div>
                <p className="text-[10.5px] font-semibold text-stone-800 mb-1 leading-snug">{v.subtitle}</p>
                <p className="text-[10px] text-stone-600 leading-snug">{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Big Bold Pricing ─── */}
        <section className="pt-3.5">
          <div className="rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-3"
               style={{ backgroundColor: GH_BLUE, color: 'white' }}>
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.35em] uppercase text-amber-200 mb-1.5">One flat price</p>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl font-light leading-none" style={pf}>₹11,999</span>
                <span className="text-sm text-stone-200">/ year</span>
              </div>
              <p className="text-[12px] text-stone-100 mt-3 leading-snug">
                One price for any property upto <strong>10 rooms</strong>.
                Annual pre-paid. No hidden costs.
              </p>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="text-[10px] tracking-[0.25em] uppercase text-amber-200 mb-1">Includes</p>
              <ul className="text-[11px] text-stone-100 space-y-0.5">
                <li className="flex items-center gap-2 justify-end"><Check className="h-3.5 w-3.5 text-amber-200" strokeWidth={3} /> GoHimalayan page + WhatsApp booking</li>
                <li className="flex items-center gap-2 justify-end"><Check className="h-3.5 w-3.5 text-amber-200" strokeWidth={3} /> OHSM Property Management App</li>
                <li className="flex items-center gap-2 justify-end"><Check className="h-3.5 w-3.5 text-amber-200" strokeWidth={3} /> Regular feature on our IG &amp; FB</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ─── Savings — tick marks throughout ─── */}
        <section className="pt-4">
          <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-2">What this saves you · vs. buying separately</p>
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
            <ul className="space-y-1">
              {SAVINGS.map((s) => (
                <li key={s.label} className="flex items-start gap-2 text-[10.5px]">
                  <Check className="h-3 w-3 flex-shrink-0 mt-0.5" style={{ color: GH_BLUE }} strokeWidth={3} />
                  <div className="flex-1 flex justify-between gap-3">
                    <span className="text-stone-700">{s.label}</span>
                    <span className="text-stone-500 text-right whitespace-nowrap">{s.v}</span>
                  </div>
                </li>
              ))}
              <li className="border-t border-stone-300 pt-1.5 mt-1 flex items-start gap-2 text-[11.5px]">
                <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: GH_BLUE }} strokeWidth={3} />
                <div className="flex-1 flex justify-between gap-3 font-bold" style={{ color: GH_BLUE }}>
                  <span>You pay for the bundle</span>
                  <span>₹11,999 / year — you save well over ₹50,000</span>
                </div>
              </li>
            </ul>
          </div>
        </section>

        {/* ─── Not included ─── */}
        <section className="pt-2.5">
          <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-1">What is not part of this bundle</p>
          <ul className="text-[10px] text-stone-600 space-y-0.5">
            {EXCLUDED.map((e) => (
              <li key={e} className="flex gap-2">
                <X className="h-3 w-3 text-stone-400 flex-shrink-0 mt-0.5" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── CTA — two step-by-step links ─── */}
        <section className="pt-3 mt-2 border-t border-stone-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Step 1 */}
            <a href={APPLY_URL} target="_blank" rel="noreferrer"
               className="bg-white border-2 rounded-xl p-2.5 hover:shadow-md transition-shadow"
               style={{ borderColor: GH_BLUE }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center rounded-full flex-shrink-0 text-[10px] font-bold"
                     style={{ backgroundColor: GH_BLUE, color: 'white', width: '18px', height: '18px' }}>1</div>
                <p className="text-[9px] tracking-[0.25em] uppercase" style={{ color: GH_BLUE }}>List your property</p>
              </div>
              <p className="text-[11px] font-semibold text-stone-900 leading-tight">gohimalayan.com/apply</p>
              <p className="text-[9.5px] text-stone-500 mt-0.5 leading-snug">Fill the form. Our team will curate your page within a week.</p>
            </a>
            {/* Step 2 */}
            <a href={OHSM_URL} target="_blank" rel="noreferrer"
               className="bg-white border-2 rounded-xl p-2.5 hover:shadow-md transition-shadow"
               style={{ borderColor: GH_BLUE }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center rounded-full flex-shrink-0 text-[10px] font-bold"
                     style={{ backgroundColor: GH_BLUE, color: 'white', width: '18px', height: '18px' }}>2</div>
                <p className="text-[9px] tracking-[0.25em] uppercase" style={{ color: GH_BLUE }}>Download the app</p>
              </div>
              <p className="text-[11px] font-semibold text-stone-900 leading-tight">ohsm.in</p>
              <p className="text-[9.5px] text-stone-500 mt-0.5 leading-snug">Get OHSM on your phone. We’ll help you set it up.</p>
            </a>
            {/* Step 3 - WhatsApp */}
            <a href={WA_LINK} target="_blank" rel="noreferrer"
               className="rounded-xl p-2.5 hover:opacity-95 transition-opacity"
               style={{ backgroundColor: GH_BLUE, color: 'white' }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center rounded-full flex-shrink-0 text-[10px] font-bold"
                     style={{ backgroundColor: 'white', color: GH_BLUE, width: '18px', height: '18px' }}>3</div>
                <p className="text-[9px] tracking-[0.25em] uppercase text-amber-200">Or just message us</p>
              </div>
              <p className="text-[14px] font-bold leading-tight whitespace-nowrap">+91 98711 55963</p>
              <p className="text-[9.5px] text-stone-100 mt-0.5">WhatsApp <ArrowRight className="h-2.5 w-2.5 inline" /> we’ll walk you through everything.</p>
            </a>
          </div>
        </section>

        {/* ─── Footer ─── */}
        <footer className="pt-2 text-[9px] text-stone-500 flex justify-between">
          <span>GoHimalayan Editorial Commons · gohimalayan.com</span>
          <span>Powered by OHSM · Startup India recognised PMS</span>
        </footer>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .print\\:hidden { display: none !important; }
          body * { visibility: hidden; }
          #host-offer-doc, #host-offer-doc * { visibility: visible; }
          #host-offer-doc { position: absolute; left: 0; top: 0; margin: 0 !important; box-shadow: none !important; }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  );
}
